//component hook 처리

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useIsClient, useMediaQuery } from 'usehooks-ts'
import { debounce, throttle } from 'lodash-es'
import { useStores } from 'stores/globals'
import { Key, mutate } from 'swr'
import { emitter } from 'infra/event-emitter'
import { PreferenceStore } from 'stores/scouts/preference'
import {
  ApplicationDetail,
  ConditionForm,
  DashboardApplicationDetail,
  DashboardScoutProposalDetail,
  RequestFormOptions,
  ScoutProposalDetail,
  WithIdNameParentId,
} from 'types/common'
import { theme } from 'infra/theme'
import Cookies from 'js-cookie'
import { COOKIE_KEY_ADMIN, COOKIE_KEY_GUEST } from 'infra/constants'
import { ChatRoomStore } from 'stores/chat-rooms/chat-room'
import { useRouter } from 'next/router'
import { useVerifiedSalaryHistories } from 'apis/hooks'
import {
  RoutePaths,
  RouterQueryKey,
  SalaryAnalysisFormStep,
} from 'types/router'
import toast from 'react-hot-toast'
import { getGlobalAttr } from 'utils/handlers'

export const useIsDesktopCSR = () => useMediaQuery(theme.deviceSize.desktop)

// NOTE(JUNSEULGI): https://chana.tistory.com/entry/Nextjs-mobile-responsive-react-responsive
export const useIsDesktop = () => {
  const [isDesktop, setIsDesktop] = useState(true)
  const matches = useMediaQuery(theme.deviceSize.desktop)
  useEffect(() => {
    setIsDesktop(matches)
  }, [matches])
  return isDesktop
}

// https://www.carlrippon.com/using-lodash-debounce-with-react-and-ts/
export const useDebounce = (timer: number, callback: Function) => {
  return useRef(
    debounce((...props) => {
      callback(...props)
    }, timer),
  ).current
}

export const useThrottle = (timer: number, callback: Function) => {
  return useRef(
    throttle((...props) => {
      callback(...props)
    }, timer),
  ).current
}

// NOTE(gogo): 이 값을 사용하는 곳은 현재 observer 를 써야 함
// TODO(gogo): mobx 상태를 훅 값으로 받아서 쓸 수 있게 만들기
export const useIsLoggedIn = () => {
  const { loginStore } = useStores()
  const isClient = useIsClient()
  // hydration mismatch 문제를 해결하기 위해 isClient 를 체크한다
  // => 최초 클라이언트 렌더 이후에 isClient 가 true 로 바뀜
  // hydration mismatch 란: SSR 내용과 CSR 내용이 서로 다른 경우 에러 발생
  return isClient ? loginStore._isLoggedIn : false
}

export const useIsLoggedInAdmin = () => {
  const isClient = useIsClient()
  return isClient ? !!Cookies.get(COOKIE_KEY_ADMIN) : false
}

export const useIsLoggedInGuest = () => {
  const isClient = useIsClient()
  return isClient ? !!Cookies.get(COOKIE_KEY_GUEST) : false
}

export const useIsLoggedInUserOrGuest = () => {
  const isLoggedIn = useIsLoggedIn()
  const isLoggedInGuest = useIsLoggedInGuest()
  return isLoggedIn || isLoggedInGuest
}

export const useIsLoggedInStartup = () => {
  const { loginStore } = useStores()
  const isClient = useIsClient()
  return isClient ? loginStore._isLoggedInStartup : false
}

export const useInfiniteScroll = (countUp: () => void) => {
  const observer = useRef<IntersectionObserver | null>(null)
  const lastElementRef = useCallback(
    (node: HTMLDivElement) => {
      if (observer.current) observer.current.disconnect()
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          countUp()
          observer?.current?.disconnect() // 무한 스크롤에 사용할 때는 한번 감지하고 나서 disconnect 해버림
        }
      })
      if (node) observer.current.observe(node)
    },
    [observer, countUp],
  )
  return lastElementRef
}

export const useObserver = <T extends HTMLElement>(
  observerCallback: (entries: IntersectionObserverEntry[]) => void,
) => {
  const observer = useRef<IntersectionObserver | null>(null)
  const observingTarget = useCallback(
    (node: T) => {
      if (observer.current) observer.current.disconnect()
      observer.current = new IntersectionObserver(observerCallback)
      if (node) observer.current.observe(node)
    },
    [observerCallback],
  )
  return observingTarget
}

export const usePositionIsInterestedUpdate = (
  positionIds: number[],
  key: Key,
) => {
  return useEffect(() => {
    const set = new Set(positionIds)
    const listenerId = emitter.positionIsInterestedChanged.on(
      ({ positionId }) => {
        if (set.has(positionId)) mutate(key)
      },
    )
    return () => emitter.positionIsInterestedChanged.off(listenerId)
  }, [positionIds, key])
}

export const useStartupIsInterestedUpdate = (
  startupIds: number[],
  key: Key,
) => {
  return useEffect(() => {
    const set = new Set(startupIds)
    const listenerId = emitter.startupIsInterestedChanged.on(
      ({ startupId }) => {
        if (set.has(startupId)) mutate(key)
      },
    )
    return () => emitter.startupIsInterestedChanged.off(listenerId)
  }, [startupIds, key])
}

export const usePreferenceRequestForm = (
  request: ConditionForm | null | undefined,
  options: RequestFormOptions | undefined,
) => {
  const preference = useRef(new PreferenceStore())

  useEffect(() => {
    if (!request || !options) return
    preference.current.setData(request, options)
    preference.current.setConditions()
  }, [request, options])

  return preference.current
}

export const useIntegratedChatRoom = (
  proposal?: ScoutProposalDetail | DashboardScoutProposalDetail,
  application?: ApplicationDetail | DashboardApplicationDetail,
) => {
  const chatRoomStore = useRef(new ChatRoomStore())

  useEffect(() => {
    chatRoomStore.current.setData(proposal, application)
  }, [proposal, application])

  return chatRoomStore.current
}

export const usePageScroll = (
  onScrollDown: () => void,
  onScrollUp?: () => void,
  eventType: 'scroll' | 'touchmove' = 'scroll', // touchmove는 모바일에서만
  timer = 500,
  timingFunc = useDebounce,
) => {
  let prevScrollTop = 0
  const scroll = timingFunc(timer, () => {
    const nowScrollTop = document.documentElement.scrollTop
    if (nowScrollTop > prevScrollTop) onScrollDown()
    else onScrollUp?.()
    prevScrollTop = nowScrollTop
  })

  useEffect(() => {
    document.addEventListener(eventType, scroll)
    return () => document.removeEventListener(eventType, scroll)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}

export const useCategorySelector = (
  data: WithIdNameParentId[],
  selectedData: WithIdNameParentId[],
  selectedParentId: number | null, // state값 사용
) => {
  const parentData = useMemo(
    () => data?.filter((item) => !item.parentId) || [],
    [data],
  )

  // 선택된 대분류의 포지션
  const childrenOfSelectedParent = useMemo(
    () =>
      (selectedParentId &&
        data.filter((item) => selectedParentId === item.parentId)) ||
      [],
    [data, selectedParentId],
  )

  const isParentFullySelected = useMemo(
    () =>
      childrenOfSelectedParent.filter(
        (item) => item.parentId === selectedParentId,
      ).length ===
      selectedData.filter((item) => item.parentId === selectedParentId).length,
    [childrenOfSelectedParent, selectedData, selectedParentId],
  )

  return {
    parentData,
    childrenOfSelectedParent,
    isParentFullySelected,
  }
}

export const useIsNewProfileSearch = () => {
  const router = useRouter()
  return (
    router.pathname.includes('/scouts/search') || // 검색 결과 페이지
    (router.pathname.includes('scouts/profile') &&
      router.query.ref === 'newProfileSearch') // 검색 결과 페이지에서 새 탭으로 열기를 눌러 프로필 상세 페이지로 이동한 경우
  )
}

export const useInterval = (callback: () => void, delay: number | null) => {
  const savedCallback = useRef<() => void>()

  // callback이 바뀔 때마다 최신 콜백으로 업데이트
  useEffect(() => {
    savedCallback.current = callback
  }, [callback])

  // delay가 null이 아니면 setInterval, 언마운트 또는 delay 변경 시 clearInterval
  useEffect(() => {
    if (delay === null) {
      return
    }
    const id = setInterval(() => {
      if (savedCallback.current) {
        savedCallback.current()
      }
    }, delay)
    return () => clearInterval(id)
  }, [delay])
}

export const useDebouncedValue = <T>(value: T, delay: number): T => {
  const [debouncedValue, setDebouncedValue] = useState(value)
  const debounce = useDebounce(delay, (v: T) => {
    setDebouncedValue(v)
  })

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => debounce(value), [value])

  return debouncedValue
}

// 토큰이 만료되거나 비정상적인 루트로 페이지에 접근하여, 경력+연봉 인증 요청 후 접근 가능한 페이지에 잘못 머무는 경우 관리
export const useIsSalaryVerificationRequestInvalid = () => {
  const { status } = useVerifiedSalaryHistories()
  const isClient = useIsClient()
  const isLoggedInUserOrGuest = useIsLoggedInUserOrGuest()
  const router = useRouter()
  const isInvalid = (isClient && !isLoggedInUserOrGuest) || status === 201
  useEffect(() => {
    if (!isInvalid) return
    toast('먼저 경력 불러오기를 요청해주세요.')
    router.replace({
      pathname: RoutePaths.SALARY_ANALYSIS_FORM,
      query: { [RouterQueryKey.STEP]: SalaryAnalysisFormStep.REQUEST },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInvalid])
  return isInvalid
}

interface IntersectionMetrics {
  ratio: number // IntersectionObserver의 intersectionRatio
  progress: number // 뷰포트 대비 보이는 비율 (0~1)
  isFullyVisible: boolean // 여유 포함 100% 가시
}

export const useIntersectionMetrics = (
  ref: React.RefObject<HTMLElement>,
): IntersectionMetrics => {
  const [ratio, setRatio] = useState(0)
  const [progress, setProgress] = useState(0)
  const [isFullyVisible, setIsFullyVisible] = useState(false)

  // 촘촘한 thresholds로 IO 콜백 빈도 확보 (0.00 ~ 1.00)
  const thresholds = useMemo(
    () => Array.from({ length: 101 }, (_, i) => i / 100),
    [],
  )

  useEffect(() => {
    const el = ref.current
    if (!el) return

    const io = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        const r = entry.intersectionRatio ?? 0
        setRatio(r)

        // progress 계산 (뷰포트 대비 보이는 높이 / 뷰포트 높이)
        const rb = entry.rootBounds
        const rt = rb?.top ?? 0
        const rbBottom = rb ? rb.top + rb.height : window.innerHeight
        const vh = rb?.height ?? window.innerHeight
        const { top, bottom } = entry.boundingClientRect
        const visibleH = Math.max(
          0,
          Math.min(bottom, rbBottom) - Math.max(top, rt),
        )
        const p = Math.min(1, Math.max(0, visibleH / vh))
        setProgress(p)

        let fv = r >= 0.999
        if (rb && !fv) {
          if (top >= rt + 1 && bottom <= rbBottom - 1) fv = true
        }
        setIsFullyVisible(fv)
      },
      { threshold: thresholds },
    )

    io.observe(el)
    return () => io.disconnect()
  }, [ref, thresholds])

  return { ratio, progress, isFullyVisible }
}

// useSnapFromMetrics 내부에서 쓰는 임시 body 락 유틸
const startTempBodyLock = () => {
  const body = document.body
  const prevOverflow = body.style.overflow
  const prevTouch = body.style.touchAction
  body.style.overflow = 'hidden'
  body.style.touchAction = 'none'

  // 스크롤 이벤트 차단
  const prevent = (e: Event) => e.preventDefault()
  const onKey = (e: KeyboardEvent) => {
    const keys = [
      'ArrowUp',
      'ArrowDown',
      'PageUp',
      'PageDown',
      'Home',
      'End',
      ' ',
    ]
    if (keys.includes(e.key)) {
      e.preventDefault()
    }
  }

  window.addEventListener('wheel', prevent, { passive: false })
  window.addEventListener('touchmove', prevent, { passive: false })
  document.addEventListener('touchmove', prevent, { passive: false }) // ios/safari 대응
  window.addEventListener('keydown', onKey)

  // 해제 함수 리턴
  return () => {
    window.removeEventListener('wheel', prevent)
    window.removeEventListener('touchmove', prevent)
    document.removeEventListener('touchmove', prevent) // ios/safari 대응
    window.removeEventListener('keydown', onKey)
    body.style.overflow = prevOverflow
    body.style.touchAction = prevTouch
  }
}

const VISIBLE_THRESHOLD = 0.5 // container가 뷰포트에 이 비율 이상 보여야 스크롤 제어 시작

// IntersectionObserver 완전가시/진행도 기반 조기 스냅/이탈 관리
export const useSnapFromMetrics = (
  ref: React.RefObject<HTMLElement>,
  metrics: IntersectionMetrics,
  suppressAttrName?: string,
  setLocked?: (v: boolean) => void, // 외부 락 setter(있으면 그걸 사용),
) => {
  const isDesktop = useIsDesktop()
  const lockForMs = isDesktop ? 1200 : 800 // 스냅 직후 바디 임시 락 유지 시간 (ms)
  const prevProgressRef = useRef(0)
  const snappingRef = useRef(false)
  const tempLockedRef = useRef(false)
  const tempUnlockRef = useRef<null | (() => void)>(null)
  const tempUnlock = () => {
    if (tempUnlockRef.current) {
      const fn = tempUnlockRef.current
      tempUnlockRef.current = null
      tempLockedRef.current = false
      fn()
    }
  }

  useEffect(() => () => tempUnlock(), [])

  useEffect(() => {
    const el = ref.current
    if (!el) return

    // 전역 억제 중이면 스냅/락 비활성
    if (suppressAttrName && getGlobalAttr(suppressAttrName)) {
      snappingRef.current = false
      tempUnlock() // 임시 락 해제
      prevProgressRef.current = metrics.progress
      return
    }

    // 100% 가시면 스냅 필요 없음 + 임시 락은 유지해도 무방
    if (metrics.isFullyVisible) {
      snappingRef.current = false
      prevProgressRef.current = metrics.progress
      return
    }

    // 조기 스냅: "뷰포트 대비 가시비율"이 VISIBLE_THRESHOLD를 상향 돌파할 때만
    const crossedUp =
      prevProgressRef.current < VISIBLE_THRESHOLD &&
      metrics.progress >= VISIBLE_THRESHOLD
    if (crossedUp && !snappingRef.current) {
      snappingRef.current = true

      // 스냅 직후 임시 락: setLocked가 있으면 그걸 쓰고, 없으면 body 임시 락으로 대체
      if (!tempLockedRef.current) {
        tempLockedRef.current = true
        if (setLocked) {
          setLocked(true)
          const t = setTimeout(() => {
            tempUnlockRef.current = null
            tempLockedRef.current = false
          }, lockForMs)
          tempUnlockRef.current = () => clearTimeout(t)
        } else {
          const unlock = startTempBodyLock()
          const t = setTimeout(() => {
            unlock()
            tempUnlockRef.current = null
            tempLockedRef.current = false
          }, lockForMs)
          tempUnlockRef.current = () => {
            unlock()
            clearTimeout(t)
          }
        }
      }

      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      setTimeout(() => (snappingRef.current = false), 500)
    }

    if (metrics.ratio === 0) snappingRef.current = false
    prevProgressRef.current = metrics.progress
  }, [ref, metrics, suppressAttrName, setLocked, lockForMs])
}
